/**
 * Centralized SEO utilities for the SG7 platform.
 * OG locale mapping, hreflang generation, date locale helpers.
 */

import { SUPPORTED_LANGUAGES } from '@/lib/languageConstants';

const SITE_URL = 'https://studiogool7.com';

// ─── OG Locale Map (OpenGraph format: ll_CC) ───────────────────────────
const OG_LOCALE_MAP: Record<string, string> = {
  'en': 'en_GB',
  'pt': 'pt_PT',
  'pt-BR': 'pt_BR',
  'fr': 'fr_FR',
  'es': 'es_ES',
  'de': 'de_DE',
  'it': 'it_IT',
  'zh-CN': 'zh_CN',
  'ja': 'ja_JP',
  'ko': 'ko_KR',
  'ar': 'ar_SA',
  'tr': 'tr_TR',
  'ru': 'ru_RU',
  'th': 'th_TH',
  'hi': 'hi_IN',
};

/** Get og:locale value for the current i18n language */
export function getOgLocale(lang: string): string {
  const base = lang.split('-')[0];
  return OG_LOCALE_MAP[lang] || OG_LOCALE_MAP[base] || 'en_GB';
}

/** Get all og:locale:alternate values (excluding current) */
export function getOgLocaleAlternates(currentLang: string): string[] {
  const current = getOgLocale(currentLang);
  return Object.values(OG_LOCALE_MAP).filter(l => l !== current);
}

// ─── Date Locale Map (Intl format: ll-CC) ──────────────────────────────
const DATE_LOCALE_MAP: Record<string, string> = {
  'en': 'en-GB',
  'pt': 'pt-PT',
  'pt-BR': 'pt-BR',
  'fr': 'fr-FR',
  'es': 'es-ES',
  'de': 'de-DE',
  'it': 'it-IT',
  'zh-CN': 'zh-CN',
  'ja': 'ja-JP',
  'ko': 'ko-KR',
  'ar': 'ar-SA',
  'tr': 'tr-TR',
  'ru': 'ru-RU',
  'th': 'th-TH',
  'hi': 'hi-IN',
};

/** Get Intl locale string for date/time formatting */
export function getDateLocale(lang: string): string {
  const base = lang.split('-')[0];
  return DATE_LOCALE_MAP[lang] || DATE_LOCALE_MAP[base] || 'en-GB';
}

// ─── Hreflang ──────────────────────────────────────────────────────────

/** BCP 47 language tag mapping for hreflang */
const HREFLANG_MAP: Record<string, string> = {
  'en': 'en',
  'pt': 'pt',
  'pt-BR': 'pt-BR',
  'fr': 'fr',
  'es': 'es',
  'de': 'de',
  'it': 'it',
  'zh-CN': 'zh-Hans',
  'ja': 'ja',
  'ko': 'ko',
  'ar': 'ar',
  'tr': 'tr',
  'ru': 'ru',
  'th': 'th',
  'hi': 'hi',
};

export interface HreflangEntry {
  hreflang: string;
  href: string;
}

/** Generate hreflang link entries for a given path (e.g. "/" or "/journal") */
export function getHreflangEntries(path: string = '/'): HreflangEntry[] {
  const url = `${SITE_URL}${path}`;
  const entries: HreflangEntry[] = SUPPORTED_LANGUAGES.map(lang => ({
    hreflang: HREFLANG_MAP[lang.code] || lang.code,
    href: url,
  }));
  // x-default
  entries.push({ hreflang: 'x-default', href: url });
  return entries;
}

// ─── Full inLanguage list for JSON-LD ──────────────────────────────────
export const ALL_LANGUAGES = SUPPORTED_LANGUAGES.map(l => l.code);

/** Get BCP 47 lang tag for <html lang> attribute */
export function getHtmlLang(lang: string): string {
  return HREFLANG_MAP[lang] || lang;
}
